import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

class APIService {
  // Fetch all documents from Notion
  async getAllDocuments() {
    try {
      const response = await axios.get(`${API}/documents`);
      return response.data;
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  }

  // Get unique visa types
  async getVisaTypes() {
    try {
      const response = await axios.get(`${API}/documents/visa-types`);
      return response.data.visa_types;
    } catch (error) {
      console.error('Error fetching visa types:', error);
      throw error;
    }
  }

  // Filter documents by visa type
  async filterDocuments(country = null, visaType = null) {
    try {
      const params = {};
      if (country) params.country = country;
      if (visaType) params.visa_type = visaType;
      
      const response = await axios.get(`${API}/documents/filter`, { params });
      return response.data;
    } catch (error) {
      console.error('Error filtering documents:', error);
      throw error;
    }
  }

  // Create payment intent
  async createPaymentIntent(amountCents, documentCount = 1) {
    try {
      const response = await axios.post(`${API}/payment/create-intent`, {
        amount_cents: amountCents,
        document_count: documentCount
      });
      return response.data;
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
    }
  }

  // Create Stripe checkout session (new)
  async createCheckoutSession(packageId = null, documentCount = null) {
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payment/create-checkout`, {
        package_id: packageId,
        document_count: documentCount,
        origin_url: originUrl
      });
      return response.data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  // ============== PAYPAL METHODS ==============

  // Create PayPal order
  async createPayPalOrder(documentCount, useMock = false) {
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payment/paypal/create-order`, {
        document_count: documentCount,
        origin_url: originUrl,
        use_mock: useMock
      });
      return response.data;
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      throw error;
    }
  }

  // Capture PayPal payment
  async capturePayPalOrder(orderId, useMock = false) {
    try {
      const response = await axios.post(`${API}/payment/paypal/capture/${orderId}?use_mock=${useMock}`);
      return response.data;
    } catch (error) {
      console.error('Error capturing PayPal order:', error);
      throw error;
    }
  }

  // Get PayPal order details
  async getPayPalOrder(orderId, useMock = false) {
    try {
      const response = await axios.get(`${API}/payment/paypal/order/${orderId}?use_mock=${useMock}`);
      return response.data;
    } catch (error) {
      console.error('Error getting PayPal order:', error);
      throw error;
    }
  }

  // Get available payment providers
  async getPaymentProviders() {
    try {
      const response = await axios.get(`${API}/payment/providers`);
      return response.data;
    } catch (error) {
      console.error('Error getting payment providers:', error);
      throw error;
    }
  }

  // Get payment status
  async getPaymentStatus(sessionId) {
    try {
      const originUrl = window.location.origin;
      const response = await axios.get(`${API}/payment/status/${sessionId}`, {
        params: { origin_url: originUrl }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting payment status:', error);
      throw error;
    }
  }

  // Get available payment packages
  async getPaymentPackages() {
    try {
      const response = await axios.get(`${API}/payment/packages`);
      return response.data;
    } catch (error) {
      console.error('Error fetching packages:', error);
      throw error;
    }
  }

  // Analyze document preview (FREE - no payment required)
  async analyzeDocumentPreview(file, sessionId = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (sessionId) {
        formData.append('session_id', sessionId);
      }

      const response = await axios.post(`${API}/documents/analyze-preview`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000  // 2 minute timeout for AI processing
      });
      return response.data;
    } catch (error) {
      console.error('Error analyzing document:', error);
      throw error;
    }
  }

  // Process document (PAID)
  async processDocument(file, paymentIntentId, documentCategory = 'general', authToken = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('payment_intent_id', paymentIntentId);
      formData.append('document_category', documentCategory);

      const headers = {
        'Content-Type': 'multipart/form-data'
      };
      
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await axios.post(`${API}/documents/process`, formData, { headers });
      return response.data;
    } catch (error) {
      console.error('Error processing document:', error);
      throw error;
    }
  }

  // Process pending document in MOCK mode (no payment required)
  async processPendingDocumentMock(pendingId) {
    try {
      const response = await axios.post(
        `${API}/documents/process-pending-mock/${pendingId}`,
        null,
        { timeout: 120000 }
      );
      return response.data;
    } catch (error) {
      console.error('Error processing document (mock):', error);
      throw error;
    }
  }

  // Auth: Register
  async register(email, password, name = null) {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        name
      });
      return response.data;
    } catch (error) {
      console.error('Error registering:', error);
      throw error;
    }
  }

  // Auth: Login
  async login(email, password) {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      return response.data;
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  }

  // Get document history
  async getDocumentHistory(authToken) {
    try {
      const response = await axios.get(`${API}/documents/history`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching history:', error);
      throw error;
    }
  }

  // Upload document pending payment
  async uploadPendingDocument(file, expectedDocumentType = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (expectedDocumentType) {
        formData.append('expected_document_type', expectedDocumentType);
      }

      const response = await axios.post(`${API}/documents/upload-pending`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  // Process multiple pending documents (batch)
  async processPendingDocumentsBatch(pendingIds, sessionId) {
    try {
      const response = await axios.post(
        `${API}/documents/process-batch`,
        { pending_ids: pendingIds, session_id: sessionId },
        { timeout: 300000, withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error processing documents batch:', error);
      throw error;
    }
  }

  // Process multiple pending documents in MOCK mode (batch)
  async processPendingDocumentsBatchMock(pendingIds) {
    try {
      const response = await axios.post(
        `${API}/documents/process-batch-mock`,
        { pending_ids: pendingIds },
        { timeout: 300000, withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error processing documents batch (mock):', error);
      throw error;
    }
  }

  // Refresh pending document expiration times
  async refreshPendingDocuments(pendingIds) {
    try {
      const response = await axios.post(
        `${API}/documents/refresh-pending`,
        pendingIds,
        { timeout: 30000 }
      );
      return response.data;
    } catch (error) {
      console.error('Error refreshing pending documents:', error);
      throw error;
    }
  }

  // Verify document type matches expected type (auto-detection)
  async verifyDocumentType(pendingId) {
    try {
      const response = await axios.post(
        `${API}/documents/verify-type/${pendingId}`,
        null,
        { timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      console.error('Error verifying document type:', error);
      throw error;
    }
  }

  // Get multi-upload configuration (for bank statements, etc.)
  async getMultiUploadConfig() {
    try {
      const response = await axios.get(`${API}/documents/multi-upload-config`);
      return response.data;
    } catch (error) {
      console.error('Error fetching multi-upload config:', error);
      throw error;
    }
  }

  // ============== ANALYTICS ==============

  // Track analytics event
  async trackEvent(eventType, sessionId, userId = null, metadata = null) {
    try {
      const response = await axios.post(`${API}/analytics/track`, {
        event_type: eventType,
        session_id: sessionId,
        user_id: userId,
        metadata: metadata
      });
      return response.data;
    } catch (error) {
      // Silently fail analytics - don't break user flow
      console.warn('Analytics tracking failed:', error);
      return { status: 'error' };
    }
  }

  // Get analytics dashboard data
  async getAnalyticsDashboard(days = 30) {
    try {
      const response = await axios.get(`${API}/analytics/dashboard?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  }

  // Get funnel metrics
  async getFunnelMetrics(days = 30) {
    try {
      const response = await axios.get(`${API}/analytics/funnel?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching funnel metrics:', error);
      throw error;
    }
  }

  // Get drop-off analysis
  async getDropoffAnalysis(days = 30) {
    try {
      const response = await axios.get(`${API}/analytics/dropoff?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching dropoff analysis:', error);
      throw error;
    }
  }

  // Process pending document after payment
  async processPendingDocument(pendingId, sessionId) {
    try {
      const response = await axios.post(
        `${API}/documents/process-pending/${pendingId}`,
        null,
        { 
          params: { session_id: sessionId },
          timeout: 120000,
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error processing document:', error);
      throw error;
    }
  }

  // Get document history for authenticated user
  async getDocumentHistory() {
    try {
      const response = await axios.get(`${API}/documents/history`, {
        withCredentials: true,
        timeout: 30000
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching document history:', error);
      throw error;
    }
  }

  // Link anonymous session analyses to the authenticated user
  async linkSessionsToUser(sessionIds) {
    try {
      const response = await axios.post(`${API}/auth/link-sessions`, 
        { session_ids: sessionIds },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error linking sessions:', error);
      throw error;
    }
  }

  // Check if a document is eligible for free reanalysis
  async checkReanalysisEligibility(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/documents/check-reanalysis`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Error checking reanalysis eligibility:', error);
      // Don't throw - allow upload to proceed
      return { is_eligible: false };
    }
  }
}

export default new APIService();
